<template>
  <div class="bulk-import">
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      accept=".csv,.xlsx,.xls"
      @change="handleFileChange"
    >

    <div class="upload-shell">
      <div class="upload-shell-header">
        <div>
          <div class="upload-title">Selecione a planilha</div>
          <div class="upload-subtitle">O arquivo deve conter uma coluna com os nomes dos tenants.</div>
        </div>
        <vs-button
          type="border"
          icon-pack="feather"
          icon="icon-upload"
          :disabled="loading"
          @click="$refs.fileInput.click()"
        >
          Selecionar arquivo
        </vs-button>
      </div>

      <div v-if="fileName" class="selected-file">
        {{ fileName }}
      </div>

      <small class="upload-help">
        Formatos aceitos: CSV, XLSX e XLS.
      </small>
    </div>

    <div v-if="hasPreview" class="bulk-preview mt-4">
      <div class="preview-inline-summary" :class="{ 'preview-inline-summary-danger': invalidCount > 0 }">
        Planilha analisada:
        <strong>{{ totalRows }}</strong> linhas,
        <strong class="text-success">{{ validCount }}</strong> válidas,
        <strong :class="invalidCount > 0 ? 'text-danger' : 'text-success'">{{ invalidCount }}</strong> inválidas
        e <strong class="text-warning">{{ ignoredCount }}</strong> ignoradas.
      </div>

      <small v-if="invalidCount > 0" class="text-danger text-xs block mt-2">
        A planilha possui inconsistências. Revise os detalhes no próximo passo antes de confirmar.
      </small>
    </div>
  </div>
</template>

<script>
import XLSX from 'xlsx'

export default {
  name: 'RequestBulkImport',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    scheduledAt: {
      type: String,
      default: ''
    },
    reason: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      fileName: '',
      loading: false,
      totalRows: 0,
      candidateRows: [],
      localInvalidItems: [],
      serverItems: [],
      serverValidated: false
    }
  },
  computed: {
    hasPreview() {
      return this.fileName !== '' && (this.serverValidated || this.candidateRows.length === 0 || this.localInvalidItems.length > 0);
    },
    previewRows() {
      return [...this.localInvalidItems, ...this.serverItems].sort((left, right) => left.index - right.index);
    },
    validCount() {
      return this.serverItems.filter(item => item.status === 'valid').length;
    },
    invalidCount() {
      return this.previewRows.filter(item => item.status !== 'valid').length;
    },
    ignoredCount() {
      return this.previewRows.filter(item => ['blank', 'duplicate'].includes(item.code)).length;
    },
    canSubmit() {
      return this.hasPreview && this.validCount > 0 && this.invalidCount === 0;
    }
  },
  watch: {
    active(val) {
      if (!val) {
        this.resetState();
      }
    }
  },
  methods: {
    async handleFileChange(event) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      this.$refs.fileInput.value = null;

      if (!file) {
        return;
      }

      if (!this.isSupportedFile(file)) {
        this.$vs.notify({
          color: 'warning',
          title: 'Planilha',
          text: 'Envie um arquivo .csv, .xlsx ou .xls.'
        });
        this.resetState();
        return;
      }

      this.loading = true;

      try {
        const rows = await this.readRows(file);
        const preview = this.buildLocalPreview(rows);

        this.fileName = file.name;
        this.totalRows = preview.totalRows;
        this.candidateRows = preview.candidateRows;
        this.localInvalidItems = preview.localInvalidItems;
        this.serverItems = [];
        this.serverValidated = false;

        if (this.candidateRows.length > 0) {
          await this.loadServerPreview();
        } else {
          this.serverValidated = true;
        }

        this.emitChange();
      } catch (error) {
        this.resetState();
        this.$vs.notify({
          color: 'danger',
          title: 'Planilha',
          text: error && error.message ? error.message : 'Não foi possível processar a planilha.'
        });
      } finally {
        this.loading = false;
      }
    },
    async loadServerPreview() {
      const payload = {
        action_type: 'tenant_deactivation',
        tenants: this.candidateRows.map(item => item.input),
        scheduled_at: this.scheduledAt || null,
        payload: {
          motivo: this.reason || ''
        }
      };

      const response = await this.$store.dispatch('approvals/previewBulkRequests', payload);
      const items = Array.isArray(response.items) ? response.items : [];

      this.serverItems = items.map((item, index) => ({
        ...item,
        index: this.candidateRows[index] ? this.candidateRows[index].index : item.index,
        input: this.candidateRows[index] ? this.candidateRows[index].input : item.input
      }));
      this.serverValidated = true;
    },
    buildLocalPreview(rows) {
      const normalizedRows = this.trimWorksheetRows(rows);
      const firstNonEmptyRowIndex = normalizedRows.findIndex(row => this.rowHasContent(row));

      if (firstNonEmptyRowIndex === -1) {
        throw new Error('A planilha está vazia.');
      }

      const firstRow = normalizedRows[firstNonEmptyRowIndex] || [];
      const { columnIndex, usesHeader } = this.resolveTenantColumn(firstRow);
      const dataRows = normalizedRows.slice(firstNonEmptyRowIndex + (usesHeader ? 1 : 0));

      if (dataRows.length === 0) {
        throw new Error('A planilha não possui tenants para importar.');
      }

      const candidateRows = [];
      const localInvalidItems = [];
      const seen = new Set();
      const startLine = firstNonEmptyRowIndex + (usesHeader ? 2 : 1);

      dataRows.forEach((row, index) => {
        const line = startLine + index;
        const input = Array.isArray(row) ? String(row[columnIndex] || '').trim() : '';
        const normalized = this.normalizeTenantValue(input);

        if (!normalized) {
          localInvalidItems.push({
            index: line,
            input,
            status: 'invalid',
            code: 'blank',
            message: 'Linha vazia ou sem tenant informado.'
          });
          return;
        }

        if (seen.has(normalized)) {
          localInvalidItems.push({
            index: line,
            input,
            status: 'invalid',
            code: 'duplicate',
            message: 'Tenant duplicado na planilha.'
          });
          return;
        }

        seen.add(normalized);
        candidateRows.push({
          index: line,
          input,
          normalized
        });
      });

      if (candidateRows.length === 0 && localInvalidItems.length === 0) {
        throw new Error('A planilha não possui tenants válidos para análise.');
      }

      return {
        totalRows: dataRows.length,
        candidateRows,
        localInvalidItems
      };
    },
    trimWorksheetRows(rows) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return [];
      }

      let start = 0;
      let end = rows.length - 1;

      while (start <= end && !this.rowHasContent(rows[start])) {
        start += 1;
      }

      while (end >= start && !this.rowHasContent(rows[end])) {
        end -= 1;
      }

      return rows.slice(start, end + 1);
    },
    rowHasContent(row) {
      return Array.isArray(row) && row.some(cell => String(cell || '').trim() !== '');
    },
    resolveTenantColumn(row) {
      const headers = row.map(cell => this.normalizeHeader(String(cell || '')));
      const knownHeaders = ['tenant', 'nome', 'fqdn', 'dashboard', 'tenant name', 'tenant_name'];
      const columnIndex = headers.findIndex(header => knownHeaders.includes(header));

      if (columnIndex > -1) {
        return {
          columnIndex,
          usesHeader: true
        };
      }

      return {
        columnIndex: 0,
        usesHeader: false
      };
    },
    normalizeHeader(value) {
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    },
    normalizeTenantValue(value) {
      let normalized = (value || '').trim().toLowerCase();
      normalized = normalized.replace(/^https?:\/\//, '');
      normalized = normalized.replace(/\/.*$/, '');

      if (normalized.includes('.')) {
        normalized = normalized.split('.')[0];
      }

      return normalized.trim();
    },
    async readRows(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
          try {
            const workbook = XLSX.read(event.target.result, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];

            if (!firstSheetName) {
              reject(new Error('A planilha está vazia.'));
              return;
            }

            const worksheet = workbook.Sheets[firstSheetName];
            const rows = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: '',
              raw: false,
            });

            resolve(rows);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error('Não foi possível ler a planilha.'));
        reader.readAsArrayBuffer(file);
      });
    },
    isSupportedFile(file) {
      return /\.(csv|xlsx|xls)$/i.test(file.name || '');
    },
    resetState() {
      this.fileName = '';
      this.totalRows = 0;
      this.candidateRows = [];
      this.localInvalidItems = [];
      this.serverItems = [];
      this.serverValidated = false;
      this.emitChange();
    },
    emitChange() {
      this.$emit('change', {
        ready: this.hasPreview,
        fileName: this.fileName,
        totalRows: this.totalRows,
        validCount: this.validCount,
        invalidCount: this.invalidCount,
        ignoredCount: this.ignoredCount,
        items: this.previewRows,
        validTenants: this.serverItems.filter(item => item.status === 'valid').map(item => item.input),
        canSubmit: this.canSubmit,
      });
    }
  }
}
</script>

<style scoped>
.upload-shell {
  padding: 14px 16px;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  background: #fafafa;
}

.upload-shell-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.upload-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.upload-subtitle {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
}

.selected-file {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 13px;
  color: #374151;
  word-break: break-word;
}

.upload-help {
  display: block;
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
}

.preview-inline-summary {
  padding: 12px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #374151;
  line-height: 1.6;
}

.preview-inline-summary-danger {
  background: #fff7f7;
  border-color: rgba(239, 68, 68, 0.18);
}

@media (max-width: 767px) {
  .upload-shell-header {
    flex-direction: column;
  }
}
</style>
